import React, { useEffect, useReducer, useContext, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { getHttpResponse } from "@/api/helper";
import { paymentMethods, accountTypes } from "@/constants";
import { AppContext } from "@/contexts/AppContext";

import MainPageBody from "./mainPageBody";

import mainReducer, { actionTypes, initialState } from "./reducer";
import { parseAmount } from "@/helper/parseAmount";
import { MainPageContext } from "@/contexts/MainPageContext";
import { filterProducts } from "@/helper/filterProducts";
import useGetUserInfoQuery from "@/hooks/queries/useGetUserInfoQuery";
import {  getItemPromotionAmount, getPromotionToApply, calculatePromotionLimits, getDecoupledItems } from "@/helper/promotions";

const MainPage = ({ resetAuthentication }) => {
    const {
        providerId,
        userId,
        token,
        companyId,
        setApplicableBenefits,
        setCompanies,
        setItbis,
        setSubTotal,
        setDiscountAmount,
        companyBranchId,
        setCompanyBranchId,
        selectedEmployee,
        setSelectedEmployee,
        benefits,
        setBenefits,
        companyAccountBenefits,
        setCompanyAccountBenefits,
        setToUseBenefits,
        setAmountToProcess,
        setCashOrCardValue,
        applicableBenefits,
    } = useContext(AppContext);
    const [state, dispatch] = useReducer(mainReducer, initialState);

    const {
        filterProductInputValue,
        users,
        choosenGeneralPromotion,
        catalogList,
        selectedCatalogIndex,
        lastProductsOrdered,
        goToComboPage,
        selectedComboItem,
        selectedCompany,
        catalogTypeId,
        catalogsCurrentDateTime,
        initialTimeLeft,
        timeLeft,
        categories,
        categoriesSelected,
        alert,
        isLoading,
        openModal,
        selectedLetter,
        selectedAccountTypeId,
        selectedPaymentMethod,
        availableBalanceToday,
        openNotEnoughBalanceMessage,
        cartAlert,
        selectedProducts
    } = state;
    
    const theme = useTheme();
    const [promotions, setPromotions] = React.useState([]);
    const { data: userInfo, isFetched: isUserInfoFetched } = useGetUserInfoQuery();
    const [selectedProductsWithPromotions, setSelectedProductsWithPromotions] = React.useState([]);
    const catalog = useMemo(()=>{
        var catalogItems = catalogList?.length > 0 ? catalogList[selectedCatalogIndex]?.items : [];
        catalogItems = catalogItems.map((item)=>{
            if(selectedAccountTypeId === accountTypes.company){
                return item;
            }
            var selectedPromotion = getPromotionToApply(item.id, promotions); 
            var promotionDiscountAmount = 0;
            promotionDiscountAmount = getItemPromotionAmount(parseFloat(item.price), 1, selectedPromotion);
            return {...item, promotion: selectedPromotion,discountAmount: promotionDiscountAmount  };
        });
        return catalogItems;
    },[promotions, catalogList, selectedCatalogIndex, selectedAccountTypeId]);
    const setAlert = (payload) => {
        dispatch({ type: actionTypes.setAlert, payload });
    };

    const setOpenModal = (payload) => {
        dispatch({ type: actionTypes.setOpenModal, payload });
    };


    useEffect(()=>{
        const productsCopy = selectedProducts.map(product => ({...product}));
        const canAddProduct = benefitsCalculation(productsCopy);
        if(canAddProduct){
            dispatch({ type: actionTypes.setSelectedProducts, payload: productsCopy });
        }
    },[selectedProducts])
    // --------------------Logic to set timer and and update it ---------------------------

    useEffect(() => {
        if (catalogsCurrentDateTime && catalogList[selectedCatalogIndex]?.dateTimeClose) {
            const secondsDifference = Math.floor(
                (new Date(catalogList[selectedCatalogIndex].dateTimeClose) - new Date(catalogsCurrentDateTime)) / 1000,
            );

            if (secondsDifference > 0) {
                dispatch({ type: actionTypes.setTimers, payload: secondsDifference });
            }
        }
    }, [catalogsCurrentDateTime, selectedCatalogIndex, catalogList]);

    useEffect(() => {
        let interval;
        if (initialTimeLeft) {
            function substractSecond() {
                dispatch({ type: actionTypes.reduceTimeLeft });
            }
            interval = setInterval(substractSecond, 1000);
        }
        return () => clearInterval(interval);
    }, [initialTimeLeft]);

    useEffect(() => {
        if (timeLeft === 0) dispatch({ type: actionTypes.clearCatalog });
    }, [timeLeft]);

    //Set available balance
    useEffect(() => {
        const thisBenefits =
            selectedAccountTypeId === accountTypes.employee
                ? benefits
                : selectedAccountTypeId === accountTypes.company
                  ? companyAccountBenefits
                  : null;
        const balancesFound = thisBenefits?.benefits?.find((benefit) => benefit.id === 1)?.balances;
        const thisAvailableBalanceToday =
            (
                balancesFound?.find((balance) => balance.name === "Balance Diario Disponible") ||
                balancesFound?.find((balance) => balance.name === "Balance Disponible")
            )?.amount || 0;
        dispatch({ type: actionTypes.setAvailableBalanceToday, payload: thisAvailableBalanceToday });
    }, [benefits, selectedAccountTypeId]);

    const secondsToTimeString = (totalSeconds) => {
        if (totalSeconds <= 0 || isNaN(totalSeconds)) {
            return "00:00:00";
        }
        let hours = Math.floor(totalSeconds / 3600);
        let minutes = Math.floor((totalSeconds - hours * 3600) / 60);
        let seconds = totalSeconds - hours * 3600 - minutes * 60;

        hours = hours < 10 ? `0${hours}` : hours;
        minutes = minutes < 10 ? `0${minutes}` : minutes;
        seconds = seconds < 10 ? `0${seconds}` : seconds;

        return `${hours}:${minutes}:${seconds}`;
    };

    const setupUserCompanies = async () => {
        try {
            const responseCompanies = (userInfo?.data?.companies || []).map((company) => ({
                companyId: company.id,
                companyName: company.companyName,
            }));
            setCompanies(responseCompanies);
            const foundPreselectedCompany = responseCompanies.find((item) => item.companyId === parseFloat(companyId));
            const selectedCompany = foundPreselectedCompany ?? responseCompanies[0];
            setSelectedEmployee({});
            dispatch({ type: actionTypes.setSelectedCompany, payload: selectedCompany });
            fetchUsers(selectedCompany.companyId);
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

  

    // ---------------------------- http requests -----------------------------------------------
    const defaultErrorMessage = "Ocurrió un error buscando los datos";
    const defaultAlertDuration = 3000;

    const httpResponseHelper = async (url) => {
        const response = await getHttpResponse(url, token, userId);
        return response;
    };

    const fetchUsers = async (companyId) => {
        dispatch({ type: actionTypes.setIsLoading, payload: true });
        try {
            const response = await httpResponseHelper(`company-service/company/${companyId}/users`);
            dispatch({ type: actionTypes.setUsers, payload: response.data.users });
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
        dispatch({ type: actionTypes.setIsLoading, payload: false });
    };

    const fetchCategories = async (providerId) => {
        dispatch({ type: actionTypes.setIsLoading, payload: true });
        try {
            const response = await httpResponseHelper(`category-service/provider/${providerId}/categories`);
            dispatch({ type: actionTypes.setCategories, payload: response.data });
            const categoriesSelectedObj = {};
            response.data.forEach((category) => (categoriesSelectedObj[category.name] = false));
            dispatch({ type: actionTypes.setCategoriesSelected, payload: categoriesSelectedObj });
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
        dispatch({ type: actionTypes.setIsLoading, payload: false });
    };

    const fetchCatalogByUserId = async (userId, providerId) => {
        dispatch({ type: actionTypes.setIsLoading, payload: true });
        try {
            const companyBranchresponse = await httpResponseHelper(`company-service/users/${userId}/company-branch`);
            setCompanyBranchId(companyBranchresponse.data.companyBranchId);
            const catalogResponse = await httpResponseHelper(
                `catalog-pos-service/provider/${providerId}/catalogs?userid=${userId}&companybranchid=${companyBranchresponse.data.companyBranchId}`,
            );
            const catalogData = catalogResponse.data.catalogs;
            if (catalogData.length === 0) {
                dispatch({ type: actionTypes.setCatalogList, payload: [] });
                dispatch({ type: actionTypes.setCatalogTypeId, payload: undefined });
                dispatch({ type: actionTypes.setCatalogsCurrentDateTime, payload: undefined });
            } else {
                dispatch({ type: actionTypes.setCatalogList, payload: catalogData });
                dispatch({
                    type: actionTypes.setCatalogsCurrentDateTime,
                    payload: catalogResponse.data.currentDateTime,
                });
                dispatch({ type: actionTypes.setSelectedCatalogIndex, payload: 0 });
                dispatch({ type: actionTypes.setCatalogTypeId, payload: catalogData[0].catalogTypeId });
            }
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
        dispatch({ type: actionTypes.setIsLoading, payload: false });
    };

    const fetchPromotions = async (userId, providerId) => {
        try {
            const response = await httpResponseHelper(
                `/promotion-service/users/${userId}/promotions?providerId=${providerId}`,
            );
            setPromotions(response.data);
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

    const fetchApplicableBenefits = async (userId) => {
        try {
            const response = await httpResponseHelper(
                `benefit-service/applicableposbenefits?providerId=${providerId}&userId=${userId}&companyId=${selectedCompany?.companyId}&companyBranchId=${companyBranchId}`,
            );
            setApplicableBenefits(response.data);
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

    const fetchUserBenefits = async (userId, catalogTypeId) => {
        try {
            const response = await httpResponseHelper(
                `benefit-service/user/${userId}/posbenefits?catalogTypeId=${catalogTypeId}&accountTypeId=1`,
            );
            setBenefits(response.data);
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

    const fetchCompanyAccountBenefits = async (userId, catalogTypeId) => {
        try {
            const response = await httpResponseHelper(
                `benefit-service/user/${userId}/posbenefits?catalogTypeId=${catalogTypeId}&accountTypeId=2`,
            );
            setCompanyAccountBenefits(response.data);
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

    const fetchLatestItems = async (userId, providerId) => {
        try {
            const response = await httpResponseHelper(
                `catalog-pos-service/provider/${providerId}/latest-items?userId=${userId}`,
            );
            dispatch({ type: actionTypes.setLastProductsOrdered, payload: response.data });
        } catch (error) {
            dispatch({
                type: actionTypes.setAlert,
                payload: {
                    open: true,
                    severity: "error",
                    message: error?.message || defaultErrorMessage,
                    duration: defaultAlertDuration,
                },
            });
        }
    };

    useEffect(() => {
        if (userInfo !== undefined && isUserInfoFetched) {
            setupUserCompanies(providerId);
        }
    }, [isUserInfoFetched]);
    useEffect(() => {
        fetchCategories(providerId);
    }, []);

    useEffect(() => {
        if (selectedEmployee.id && catalogTypeId) {
            fetchUserBenefits(selectedEmployee.id, catalogTypeId);
            fetchPromotions(selectedEmployee.id, providerId);
            fetchApplicableBenefits(selectedEmployee.id);
            if (selectedEmployee.companyAccount) {
                fetchCompanyAccountBenefits(selectedEmployee.id, catalogTypeId);
            }
        } else setBenefits();
    }, [selectedEmployee.id, catalogTypeId]);

    // ---------------------------- Functions ------------------------------------------------------

    const setFilterProductInputValueHandler = (value) => {
        dispatch({ type: actionTypes.setSelectedLetter, payload: null });
        dispatch({ type: actionTypes.setFilterProductInputValue, payload: value });
    };

    const setSelectedLetterHandler = (letter) => {
        dispatch({ type: actionTypes.setFilterProductInputValue, payload: "" });
        dispatch({ type: actionTypes.setSelectedLetter, payload: letter });
    };

    const toggleCategory = (categoryName) => {
        const previousCategoriesSelectedCopy = { ...categoriesSelected };
        previousCategoriesSelectedCopy[categoryName] = !previousCategoriesSelectedCopy[categoryName];
        dispatch({ type: actionTypes.setCategoriesSelected, payload: previousCategoriesSelectedCopy });
    };

    const changeCatalogIndexHandler = (index) => {
        dispatch({ type: actionTypes.setSelectedCatalogIndex, payload: index });
        dispatch({ type: actionTypes.setCatalogTypeId, payload: catalogList[index].catalogTypeId });
    };

    const notEnoughBalanceHandler = () => {
        dispatch({ type: actionTypes.setOpenNotEnoughBalanceMessage, payload: true });
        setTimeout(() => dispatch({ type: actionTypes.setOpenNotEnoughBalanceMessage, payload: false }), 3000);
    };

    const getProductType = (product) => {
        return product.combo ? "combos" : "adicionales";
    }
    const getProductsTotals = (products) => {
        return products.reduce(
            (acc, product) => {
                const productPrice = parseAmount(product.price) * Number(product.quantity);
                let itbis = 0;
                if (product.taxTypes[0] * 1) {
                    itbis = (parseAmount(product.price) * (product.taxTypes[0] || 0) * Number(product.quantity)) / 100;
                }

                acc.total += productPrice;
                acc.subtotal += productPrice - itbis;
                acc.itbis += itbis;

                return acc;
            },
            { total: 0, subtotal: 0, itbis: 0 },
        );
    };

    const getSubsidyLimitAmount = (currentBenefitOrder, products) => {
        const type = parseInt(currentBenefitOrder.type, 10);
        const subsidyDetail = currentBenefitOrder.subsidyDetail?.[0] || {};
        const { total, subtotal, itbis } = getProductsTotals(products);
        if (!subsidyDetail) return 0;
        let amount = getSubsidyAmount(currentBenefitOrder, total, subtotal);
        switch (type) {
            case 1:
            case 5:
                // if its a porcentage type then it should return null
                return null;
            case 3:
            case 2:
                // if its type amount or combined then it should return the amount as the limit
                amount = parseFloat(subsidyDetail.amount ?? 0);
                break;
            case 6:
                // itbis + amount will return the itbis + amount as the limit
                amount = itbis + parseFloat(subsidyDetail.amount ?? 0);
                break;
            default:
                break;
        }
        if (subsidyDetail.limitPerDayIndicator === "true" && subsidyDetail.limitPerDay) {
            const limitPerDay = parseFloat(subsidyDetail.limitPerDay) || 0;
            amount = amount > limitPerDay ? limitPerDay : amount;
        }
        return amount;
    };

    // Calculate benefits to use and whether it is possible to have current selected products (split calculation)
    const getSubsidyAmount = (currentBenefitOrder, total, subtotal, product) => {
        const type = parseInt(currentBenefitOrder.type, 10);
        const subsidyDetail = currentBenefitOrder.subsidyDetail?.[0] || {};
        if (!subsidyDetail) return 0;
        const percentage = parseFloat(subsidyDetail.percentage ?? 0);
        const fixedAmount = parseFloat(subsidyDetail.amount ?? 0);
        const itbisAmount = total - subtotal;
        let amount = parseFloat(subsidyDetail.amount);
        switch (type) {
            // Percentage Type
            case 1:
                amount = (percentage / 100) * total;
                break;
            // Fixed amount
            case 2:
                amount = Math.min(fixedAmount, total);
                break;
            // Combinado (porcentaje + monto fijo)
            case 3: {
                const percAmount = (percentage / 100) * total;
                amount = Math.min(percAmount, fixedAmount, total);
                break;
            }
            // Sin subsidio
            case 4:
                amount = 0;
                break;
            // Tipo itbis
            case 5:
                amount = itbisAmount;
                break;
            // Itbis + amount
            case 6:
                {
                    amount = Math.min(itbisAmount + fixedAmount, total);
                }
                break;
            default:
                amount = 0;
                break;
        }
        if (subsidyDetail.limitPerDayIndicator === "true" && subsidyDetail.limitPerDay) {
            const limitPerDay = parseFloat(subsidyDetail.limitPerDay) || 0;
            amount = amount > limitPerDay ? limitPerDay : amount;
        }
        if(product && currentBenefitOrder.productTypes){
            const productType = getProductType(product);
            if(!currentBenefitOrder.productTypes.includes(productType)){
                amount = 0;
            }
        }
        return amount;
    };
    const benefitsCalculation = (thisSelectedProducts) => {
        const newTaxes = {};
        let cashMethodSelected =
            selectedAccountTypeId === accountTypes.employee &&
            (selectedPaymentMethod === paymentMethods.efectivo || selectedPaymentMethod === paymentMethods.tarjeta);
        let newDiscount = 0
        let newSubtotal = 0;
        let canHaveProducts = true;
        let thisAmountToProcess = 0;
        let totalCashOrCardValue = 0; //total cash or card value when payment method is nomina
        let generalPromoBalance = choosenGeneralPromotion ? calculatePromotionLimits(choosenGeneralPromotion)?.maxPurchaseValue : null;
        let availableBalanceRemaining = availableBalanceToday;

        const benefitsOrder = (
            selectedAccountTypeId === accountTypes.employee
                ? benefits
                : selectedAccountTypeId === accountTypes.company
                  ? companyAccountBenefits
                  : null
        )?.benefitsOrder
            ?.filter((benefit) => {
                const isNotCreditLimit = benefit.benefitType !== "limiteDeCredito";
                const subsidyDisabled =
                    benefit.benefitType === "subsidio" &&
                    cashMethodSelected &&
                    !(applicableBenefits.useSubsidyPosCash ?? false);
                const assignationDisabled =
                    benefit.benefitType === "asignacion" &&
                    cashMethodSelected &&
                    !(applicableBenefits.useAssignationPosCash ?? false);
                const dailyAssignationDisabled =
                    benefit.benefitType === "asignacionDiaria" &&
                    cashMethodSelected &&
                    !(applicableBenefits.useDailyAssignationPosCash ?? false);
                return isNotCreditLimit && !(subsidyDisabled || assignationDisabled || dailyAssignationDisabled);
            })
            .sort((a, b) => a.order - b.order);
        const subsidyBenefit = benefitsOrder?.find((benefit) => benefit.benefitType === "subsidio");
        const subsidyLimitAmount = subsidyBenefit ? getSubsidyLimitAmount(subsidyBenefit, thisSelectedProducts) : 0;
        const thisToUseBenefits = {};
        const decoupledSelectedProducts = getDecoupledItems(thisSelectedProducts);
        decoupledSelectedProducts.forEach((product) => {
            let currentProductDiscount = product.promotion ? getItemPromotionAmount(parseAmount(product.price), Number(product.quantity), product.promotion) : 0;
            if(!product.promotion && generalPromoBalance > 0){
                const generalPromotionAmount = getItemPromotionAmount(parseAmount(product.price), Number(product.quantity), choosenGeneralPromotion, true);
                if(generalPromotionAmount > 0 && generalPromoBalance > 0){
                        const discountedAmount = Math.min(generalPromotionAmount, generalPromoBalance);
                        currentProductDiscount += discountedAmount;
                        generalPromoBalance -= discountedAmount;
                }
            }
            const currentProductSubtotal = (parseAmount(product.price) * Number(product.quantity)) - currentProductDiscount;
            if(!product.promotion){
                newDiscount += currentProductDiscount;
            }
            newSubtotal += currentProductSubtotal;
            let accItbis = 0;
            let currentProductItbis = 0;

            if (product.taxTypes[0] * 1) {
                //logic to set taxes
                accItbis = newTaxes[`ITBIS (${product.taxTypes[0] * 1}%)`] || 0;
                currentProductItbis =
                    (currentProductSubtotal * (product.taxTypes[0] || 0) ) / 100;
                accItbis += currentProductItbis;
                newTaxes[`ITBIS (${product.taxTypes[0] * 1}%)`] = accItbis; // set taxes
            }
            // Determine all benefits to use in each product and in total
            let thisCurrentProductBenefits = 0;
            let currentProductTotalRemaining = currentProductSubtotal + currentProductItbis;
            if (benefitsOrder) {
                for (let i = 0; i < benefitsOrder.length; i++) {
                    const currentBenefitOrder = benefitsOrder[i];
                    const isSubsidy = currentBenefitOrder.benefitType === "subsidio";
                    const currentBenefitOrderAmount = isSubsidy
                        ? getSubsidyAmount(currentBenefitOrder, currentProductTotalRemaining, currentProductSubtotal, product)
                        : currentBenefitOrder.amount;
                    if (!thisToUseBenefits[currentBenefitOrder.benefitType]) {
                        thisToUseBenefits[currentBenefitOrder.benefitType] = 0;
                    }
                    const limitBenefitAmount = isSubsidy && currentBenefitOrderAmount > 0 ? subsidyLimitAmount : currentBenefitOrderAmount;
                    if (limitBenefitAmount !== null) {
                        const currentAvailableBenefitAmount = currentBenefitOrderAmount <= 0 ? 0 :
                            limitBenefitAmount - thisToUseBenefits[currentBenefitOrder.benefitType];
                        if (currentAvailableBenefitAmount >= currentProductTotalRemaining) {
                            thisCurrentProductBenefits += currentProductTotalRemaining;
                            thisToUseBenefits[currentBenefitOrder.benefitType] += currentProductTotalRemaining;
                            currentProductTotalRemaining = 0;
                            break;
                        }

                        if (currentAvailableBenefitAmount < currentProductTotalRemaining) {
                            thisCurrentProductBenefits += currentAvailableBenefitAmount;
                            thisToUseBenefits[currentBenefitOrder.benefitType] += currentAvailableBenefitAmount;
                            currentProductTotalRemaining -= currentAvailableBenefitAmount;
                        }
                    }
                    // If the total benefits are null, means that its a value that its porcentual and doesnt have a limit
                    else {
                        thisCurrentProductBenefits += currentBenefitOrderAmount;
                        thisToUseBenefits[currentBenefitOrder.benefitType] += currentBenefitOrderAmount;
                        currentProductTotalRemaining -= currentBenefitOrderAmount;
                    }
                }
            }
            product.benefits = thisCurrentProductBenefits;

            let currentProductAmountToProcess;
            const employeeAndNominaCondition =
                selectedAccountTypeId === accountTypes.employee && selectedPaymentMethod === paymentMethods.nomina;
            const thisIsCompanyAccount = selectedAccountTypeId === accountTypes.company;

            if (thisIsCompanyAccount && currentProductTotalRemaining > availableBalanceRemaining) {
                //Return false if there is not enough balance in the company account
                canHaveProducts = false;
                return;
            }

            if (thisIsCompanyAccount || employeeAndNominaCondition) {
                currentProductAmountToProcess =
                    currentProductTotalRemaining <= availableBalanceRemaining
                        ? currentProductTotalRemaining
                        : availableBalanceRemaining;
                thisAmountToProcess += currentProductAmountToProcess;
                availableBalanceRemaining -= currentProductAmountToProcess;
            }

            if (employeeAndNominaCondition) {
                // set cash or card value in product if payment method is nomina and it's an employee account

                product.cashOrCardValue = currentProductTotalRemaining - currentProductAmountToProcess;
                totalCashOrCardValue += product.cashOrCardValue;
            } else {
                if (cashMethodSelected) {
                    product.cashOrCardValue = currentProductTotalRemaining;
                    thisAmountToProcess += currentProductTotalRemaining;
                }
            }
        });
        if (canHaveProducts) {
            setSubTotal(newSubtotal);
            setItbis(newTaxes);
            setToUseBenefits(thisToUseBenefits);
            setCashOrCardValue(totalCashOrCardValue);
            setSelectedProductsWithPromotions(decoupledSelectedProducts);
            setAmountToProcess(thisAmountToProcess);
            setDiscountAmount(newDiscount);
        } else {
            notEnoughBalanceHandler();
        }
        return {
            canHaveProducts,
        };
    };

    const addProductToCart = (product) => {
        const selectedProductsCopy = selectedProducts.map((prod) => {
            return { ...prod };
        });
        const foundProductIndex = selectedProductsCopy.findIndex((prod) => prod.id === product.id);

        if (foundProductIndex >= 0) {
            const foundProductCopy = { ...selectedProductsCopy[foundProductIndex] };
            foundProductCopy.quantity++;
            selectedProductsCopy[foundProductIndex] = foundProductCopy;
        } else {
            product.quantity = 1;
            product.cashOrCardValue = 0;
            product.deliveryDate = catalogList[selectedCatalogIndex].deliveryDate;
            product.deliveryTypeId = catalogList[selectedCatalogIndex].deliveryTypes[0].id;
            product.orderReason = "";
            product.providerId = providerId;
            product.sections = [];
            product.userType = selectedAccountTypeId;
            selectedProductsCopy.push(product);
        }

        const calculation = benefitsCalculation(selectedProductsCopy);
        if (calculation.canHaveProducts) {
            dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsCopy });
        }
    };

    const editProductQuantity = (productId, quantity, isCombo) => {
        if (quantity < 0) return;
        const selectedProductsCopy = selectedProducts.map((prod) => {
            return { ...prod };
        });
        const foundProductIndex = selectedProductsCopy.findIndex((prod) =>
            isCombo ? prod.key === productId : prod.id === productId,
        );
        const foundProductCopy = { ...selectedProductsCopy[foundProductIndex] };
        foundProductCopy.quantity = quantity;
        selectedProductsCopy[foundProductIndex] = foundProductCopy;

        const calculation = benefitsCalculation(selectedProductsCopy);
        if (calculation.canHaveProducts) {
            dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsCopy });
        }
    };

    const handleZeroQuantity = () => {
        if (selectedProducts.some((product) => product.quantity < 1)) {
            const selectedProductsCopy = selectedProducts.map((prod) => {
                return { ...prod, quantity: prod.quantity < 1 ? "1" : prod.quantity };
            });
            const calculation = benefitsCalculation(selectedProductsCopy);
            if (calculation.canHaveProducts) {
                dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsCopy });
            }
        }
    };

    const deleteProductCart = (productId) => {
        const selectedProductsFilter = selectedProducts
            .map((prod) => {
                return { ...prod };
            })
            .filter((product) => product.id !== productId); //copying and filtering
        const calculation = benefitsCalculation(selectedProductsFilter);
        if (calculation.canHaveProducts) {
            dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsFilter });
        }
    };

    const deleteComboCart = (combo) => {
        const selectedProductsFilter = selectedProducts
            .map((prod) => {
                return { ...prod };
            })
            .filter((product) => {
                //copying and filtering
                if (!product.selectedProductIndexes) return true;
                if (combo.id !== product.id) return true;
                if (combo.selectedProductIndexes === product.selectedProductIndexes) return false;
                return true;
            });

        const calculation = benefitsCalculation(selectedProductsFilter);
        if (calculation.canHaveProducts) {
            dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsFilter });
        }
    };

    const comboSelectedHandler = (comboItem) => {
        dispatch({ type: actionTypes.setGoToComboPage, payload: true });
        dispatch({ type: actionTypes.setSelectedComboItem, payload: comboItem });
    };

    const clearComboPage = () => {
        dispatch({ type: actionTypes.setGoToComboPage, payload: false });
    };

    const addComboToCart = (combo) => {
        const selectedProductsCopy = selectedProducts.map((prod) => {
            return { ...prod };
        });
        let foundSameCombo = false;
        const foundCombos = selectedProductsCopy.filter((thisCombo) => combo.id === thisCombo.id);
        let foundCombo;
        if (foundCombos.length > 0) {
            for (let i = 0; i < foundCombos.length; i++) {
                foundCombo = foundCombos[i];
                if (foundCombo.sections?.length === combo.sections?.length) {
                    foundSameCombo = true;
                    foundCombo.sections.forEach((section) => {
                        const sameSections = combo.sections.filter((thisSection) => thisSection.id === section.id);
                        if (sameSections.length === 0) {
                            foundSameCombo = false;
                            return;
                        } else if (!sameSections.some((sameSection) => sameSection.variantId === section.variantId)) {
                            foundSameCombo = false;
                            return;
                        }
                    });
                    if (foundSameCombo) break;
                }
            }

            if (foundSameCombo === true) {
                foundCombo.quantity++;
            }
        }

        if (!foundSameCombo) {
            combo.cashOrCardValue = 0;
            combo.deliveryDate = catalogList[selectedCatalogIndex].deliveryDate;
            combo.deliveryTypeId = catalogList[selectedCatalogIndex].deliveryTypes[0].id;
            combo.orderReason = "";
            combo.providerId = providerId;
            combo.userType = selectedAccountTypeId;
            combo.key = Date.now();

            selectedProductsCopy.push(combo);
        }

        const calculation = benefitsCalculation(selectedProductsCopy);
        if (calculation.canHaveProducts) {
            dispatch({ type: actionTypes.setSelectedProducts, payload: selectedProductsCopy });
        }
    };

    const addComboToCartHandler = (combo) => {
        addComboToCart(combo);
        dispatch({ type: actionTypes.setGoToComboPage, payload: false });
    };

    const clearCart = () => {
        dispatch({ type: actionTypes.setSelectedProducts, payload: [] });
        setSubTotal(0);
        setItbis({});
    };

    const commonClear = () => {
        clearCart();
        dispatch({ type: actionTypes.setSelectedAccountTypeId, payload: accountTypes.employee });
        dispatch({ type: actionTypes.commonClear });
    };

    const clearSelectedEmployee = () => {
        commonClear();
        setSelectedEmployee({});
    };

    const changeCompanyHandler = (newValue) => {
        dispatch({ type: actionTypes.setSelectedCompany, payload: newValue });
        dispatch({ type: actionTypes.setSelectedPaymentMethod, payload: paymentMethods.nomina });
        commonClear();
        setSelectedEmployee({});
        dispatch({ type: actionTypes.setUsers, payload: [] });
        fetchUsers(newValue.companyId);
    };

    const changeEmployeeHandler = (newValue) => {
        commonClear();
        setSelectedEmployee(newValue);
        changePaymentMethodHandler(paymentMethods.nomina);
        fetchCatalogByUserId(newValue.id, providerId);
        fetchLatestItems(newValue.id, providerId);
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        dispatch({ type: actionTypes.setAlert, payload: { open: false } });
    };

    const handleCloseCartAlert = () => {
        dispatch({ type: actionTypes.setCartAlert, payload: { function: null, text: null } });
    };

    const changeAccountTypeHandler = (id) => {
        const theFunction = () => {
            if (id === accountTypes.company) {
                dispatch({ type: actionTypes.setSelectedPaymentMethod, payload: paymentMethods.nomina });
            }
            dispatch({ type: actionTypes.setSelectedAccountTypeId, payload: id });
        };

        if (!selectedProducts || selectedProducts.length === 0) theFunction();
        else
            dispatch({
                type: actionTypes.setCartAlert,
                payload: {
                    function: () => {
                        theFunction();
                        dispatch({ type: actionTypes.setCartAlert, payload: { function: null, text: null } });
                        clearCart();
                    },
                    text: id === accountTypes.company ? "Cuenta Empresa" : "Nómina",
                },
            });
    };

    const changePaymentMethodHandler = (value) => {
        const theFunction = () => {
            dispatch({ type: actionTypes.setSelectedPaymentMethod, payload: value });
        };

        if (!selectedProducts || selectedProducts.length === 0) theFunction();
        else
            dispatch({
                type: actionTypes.setCartAlert,
                payload: {
                    function: () => {
                        theFunction();
                        dispatch({ type: actionTypes.setCartAlert, payload: { function: null, text: null } });
                        clearCart();
                    },
                    text:
                        value === paymentMethods.nomina
                            ? "Nómina"
                            : value === paymentMethods.efectivo
                              ? "Efectivo"
                              : "Tarjeta",
                },
            });
    };
    const filteredCatalogsProducts = useMemo(() => {
        return filterProducts(catalog, categoriesSelected, filterProductInputValue, selectedLetter);
    }, [catalog, filterProductInputValue, categoriesSelected, selectedLetter, promotions]);

    const contextValues = {
        filterProductInputValue,
        selectedProducts,
        users,
        lastProductsOrdered,
        theme,
        goToComboPage,
        selectedComboItem,
        selectedCompany,
        addProductToCart,
        deleteComboCart,
        editProductQuantity,
        deleteProductCart,
        comboSelectedHandler,
        clearComboPage,
        addComboToCartHandler,
        changeCompanyHandler,
        changeEmployeeHandler,
        setFilterProductInputValue: setFilterProductInputValueHandler,
        catalogList,
        selectedCatalogIndex,
        setSelectedCatalogIndex: changeCatalogIndexHandler,
        timeLeft: timeLeft ? secondsToTimeString(timeLeft) : "00:00:00",
        filteredCatalogsProducts,
        categories,
        categoriesSelected,
        toggleCategory,
        alert,
        handleCloseAlert,
        setAlert,
        isLoading,
        openModal,
        setOpenModal,
        selectedLetter,
        setSelectedLetter: setSelectedLetterHandler,
        setSelectedAccountTypeId: changeAccountTypeHandler,
        selectedAccountTypeId,
        selectedPaymentMethod,
        setSelectedPaymentMethod: changePaymentMethodHandler,
        openNotEnoughBalanceMessage,
        cartAlert,
        handleCloseCartAlert,
        handleZeroQuantity,
        clearSelectedEmployee,
        promotions,
        choosenGeneralPromotion,
        selectedProductsWithPromotions,
        setChoosenGeneralPromotion: (promotion) => dispatch({ type: actionTypes.setChoosenGeneralPromotion, payload: promotion }),
    };

    return (
        <MainPageContext.Provider value={contextValues}>
            <MainPageBody />
        </MainPageContext.Provider>
    );
};

export default MainPage;
